import React from 'react';

import Grid from '@mui/material/Grid2';

import {
  CreatureLimitation,
  MaterialLootEntry,
} from "@dndModels";
import { CREATURE_CRS } from "@dndModels/Constants";
import { CRAFTING_MATERIAL_TYPE_VALUES, CraftingMaterialType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import { DiceRollFields } from "@dndComponents/other/LootEntryInput";

interface Props {
  entry: MaterialLootEntry;
  disabled: boolean;
  onChange: (update: (entry: MaterialLootEntry) => void) => void;
}

const CR_OPTIONS = ["Any", ...CREATURE_CRS.filter(o => +o <= 30)];

export default function MaterialEntryInput({
  entry,
  disabled,
  onChange,
}: Props) {
  return (
    <Grid container spacing={1} direction="row">
      <Grid size={8}>
        <StringBox
          name="Name"
          value={entry.name}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.name = changed; })}
        />
      </Grid>
      <Grid size={4}>
        <StringBox
          name="Chance per Creature (%)"
          type="number"
          value={entry.chance}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.chance = Math.min(Math.max(+changed, 0), 100); })}
        />
      </Grid>
      <Grid size={12} className="header">
        Which Creatures It Comes From
      </Grid>
      <Grid size={6}>
        <SelectStringBox
          name="Min Creature CR"
          options={CR_OPTIONS}
          value={entry.min_cr < 0 ? "Any" : `${entry.min_cr}`}
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string) => onChange(o => { o.min_cr = changed === "Any" ? -1 : +changed; })}
        />
      </Grid>
      <Grid size={6}>
        <SelectStringBox
          name="Max Creature CR"
          options={CR_OPTIONS}
          value={entry.max_cr < 0 ? "Any" : `${entry.max_cr}`}
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string) => onChange(o => { o.max_cr = changed === "Any" ? -1 : +changed; })}
        />
      </Grid>
      <Grid size={12}>
        <CreatureLimitInput
          obj={entry.creature_limit}
          disabled={disabled}
          ignore_knowledge
          ignore_cr
          onChange={(changed: CreatureLimitation) => onChange(o => { o.creature_limit = changed; })}
        />
      </Grid>
      <Grid size={12} className="header">
        What It Gives
      </Grid>
      <Grid size={12}>
        <SelectStringBox
          name="Material Type"
          options={[...CRAFTING_MATERIAL_TYPE_VALUES]}
          value={entry.material_type}
          auto_sort={false}
          disabled={disabled}
          onChange={(changed: string) => onChange(o => { o.material_type = changed as CraftingMaterialType; })}
        />
      </Grid>
      <Grid size={12}>
        <DiceRollFields name="Amount" dice={entry.amount} disabled={disabled}
          onChange={update => onChange(o => update(o.amount))}
        />
      </Grid>
      <Grid size={6}>
        <StringBox
          name="Weight per Unit (lb)"
          type="number"
          value={entry.weight}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.weight = Math.max(+changed, 0); })}
        />
      </Grid>
      <Grid size={6}>
        <StringBox
          name="Value per Unit (gp)"
          type="number"
          value={entry.value}
          disabled={disabled}
          onBlur={(changed: string) => onChange(o => { o.value = Math.max(+changed, 0); })}
        />
      </Grid>
    </Grid>
  );
}
